import React, { useState, useEffect } from 'react';
import { Sparkles, Book, ArrowRight, ArrowLeft, Loader2, Heart, Star, Moon, ChevronDown, ChevronUp, Edit2, Check, X, Settings, Feather, Palette, Wand2 } from 'lucide-react';
import { apiService } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { usePayment } from '../contexts/PaymentContext';
import { useNavigation } from '../contexts/NavigationContext';
import { useUserData } from '../hooks/useUserData';
import PaymentModal from './PaymentModal';
import WelcomePage from './WelcomePage';
import StoryParametersBanner from './StoryParametersBanner';
import StandardOnboardingModal from './StandardOnboardingModal';
import StandardReminderModal from './StandardReminderModal';
import { saveStoryToMyStories } from '../utils/storyStorage';

const TRANSLATIONS = {
  "en-US": {
    "ageTitle": "Tell us about your child",
    "ageSubtitle": "This helps us create a personalized story just for them",
    "namePlaceholder": "Enter your child's first name",
    "ageGroupTitle": "Select age group:",
    "continueButton": "Continue",
    "step1of5": "Step 1 of 5",
    "genderTitle": "Tell us about your child",
    "genderSubtitle": "We'll use the right pronouns and perspective",
    "boy": "boy",
    "girl": "girl",
    "other": "other",
    "backButton": "Back",
    "step2of5": "Step 2 of 5",
    "interestsTitle": "What does your child love?",
    "interestsSubtitle": "Pick 1 thing that sparks their imagination",
    "animals": "Animals",
    "spaceStars": "Space & Stars",
    "oceanSeaLife": "Ocean & Sea Life",
    "dinosaurs": "Dinosaurs",
    "magicFantasy": "Magic & Fantasy",
    "sports": "Sports",
    "music": "Music",
    "artDrawing": "Art & Drawing",
    "natureForest": "Nature & Forest",
    "superheroes": "Superheroes",
    "vehiclesTransportation": "Vehicles & Transportation",
    "cookingFood": "Cooking & Food",
    "scienceExperiments": "Science & Experiments",
    "step3of5": "Step 3 of 5",
    "styleTitle": "What kind of story would you like?",
    "styleSubtitle": "Choose the tone that fits bedtime best",
    "funnyLabel": "Funny & Silly",
    "funnyDesc": "Giggles and laughs throughout",
    "adventurousLabel": "Adventurous & Exciting",
    "adventurousDesc": "Thrilling quests and discoveries",
    "gentleLabel": "Gentle & Calming",
    "gentleDesc": "Peaceful and soothing",
    "magicalLabel": "Magical & Enchanting",
    "magicalDesc": "Fantasy and wonder",
    "educationalLabel": "Educational & Learning",
    "educationalDesc": "Fun facts and knowledge",
    "step4of5": "Step 4 of 5",
    "lessonTitle": "What lesson should we include?",
    "lessonSubtitle": "What important message should they learn?",
    "lessonPlaceholder": "Examples: Being kind to others, trying new things, sharing is caring, being brave when scared...",
    "storyFormatTitle": "Story Length",
    "storyFormatSubtitle": "Choose how long you'd like the story to be",
    "formatShort": "Short",
    "formatShortDesc": "Quick bedtime story",
    "formatMedium": "Medium", 
    "formatMediumDesc": "More adventures & details",
    "formatLong": "Long",
    "formatLongDesc": "Rich story with deeper content",
    "createStoryButton": "Create Story",
    "step5of5": "Step 5 of 5",
    "generatingTitle": "Creating your magical story...",
    "generatingSubtitle": "Our storytelling magic is working...",
    "storyReadyTitle": "Your bedtime story is ready!",
    "storyReadySubtitle": "Sweet dreams are just a story away!",
    "sweetDreams": "Sweet dreams!",
    "createAnotherStory": "Create Another Story",
    "errorMessage": "Sorry, there was an error generating your story. Please try again."
  },
  /* LOCALE_PLACEHOLDER_START */
  "fr-FR": {
    "ageTitle": "Parlez-nous de votre enfant",
    "ageSubtitle": "Cela nous aide à créer une histoire personnalisée rien que pour lui/elle",
    "namePlaceholder": "Entrez le prénom de votre enfant",
    "ageGroupTitle": "Sélectionnez la tranche d'âge :",
    "continueButton": "Continuer",
    "step1of5": "Étape 1 sur 5",
    "genderTitle": "Parlez-nous de votre enfant",
    "genderSubtitle": "Nous utiliserons les bons pronoms et la bonne perspective",
    "boy": "garçon",
    "girl": "fille",
    "other": "autre",
    "backButton": "Retour",
    "step2of5": "Étape 2 sur 5",
    "interestsTitle": "Qu'est-ce que votre enfant adore ?",
    "interestsSubtitle": "Choisissez 1 chose qui éveille son imagination",
    "animals": "Animaux",
    "spaceStars": "Espace et Étoiles",
    "oceanSeaLife": "Océan et Vie Marine",
    "dinosaurs": "Dinosaures",
    "magicFantasy": "Magie et Fantastique",
    "sports": "Sports",
    "music": "Musique",
    "artDrawing": "Art et Dessin",
    "natureForest": "Nature et Forêt",
    "superheroes": "Super-héros",
    "vehiclesTransportation": "Véhicules et Transport",
    "cookingFood": "Cuisine et Nourriture",
    "scienceExperiments": "Science et Expériences",
    "step3of5": "Étape 3 sur 5",
    "styleTitle": "Quel type d'histoire souhaitez-vous ?",
    "styleSubtitle": "Choisissez le ton qui convient le mieux à l'heure du coucher",
    "funnyLabel": "Drôle et Rigolote",
    "funnyDesc": "Des rires et de la joie tout au long",
    "adventurousLabel": "Aventureuse et Excitante",
    "adventurousDesc": "Des quêtes palpitantes et des découvertes",
    "gentleLabel": "Douce et Apaisante",
    "gentleDesc": "Paisible et relaxante",
    "magicalLabel": "Magique et Enchanteresse",
    "magicalDesc": "Fantaisie et émerveillement",
    "educationalLabel": "Éducative et Instructive",
    "educationalDesc": "Faits amusants et connaissances",
    "step4of5": "Étape 4 sur 5",
    "lessonTitle": "Quelle leçon devrions-nous inclure ?",
    "lessonSubtitle": "Quel message important devrait-il apprendre ?",
    "lessonPlaceholder": "Exemples : Être gentil avec les autres, essayer de nouvelles choses, partager c'est important, être courageux quand on a peur...",
    "storyFormatTitle": "Longueur de l'Histoire",
    "storyFormatSubtitle": "Choisissez la durée souhaitée pour l'histoire",
    "formatShort": "Courte",
    "formatShortDesc": "Histoire rapide du soir",
    "formatMedium": "Moyenne", 
    "formatMediumDesc": "Plus d'aventures et de détails",
    "formatLong": "Longue",
    "formatLongDesc": "Histoire riche avec contenu approfondi",
    "createStoryButton": "Créer l'Histoire",
    "step5of5": "Étape 5 sur 5",
    "generatingTitle": "Création de votre histoire magique...",
    "generatingSubtitle": "Notre magie de conteur opère...",
    "storyReadyTitle": "Votre histoire du soir est prête !",
    "storyReadySubtitle": "De beaux rêves ne sont qu'à une histoire de distance !",
    "sweetDreams": "Beaux rêves !",
    "createAnotherStory": "Créer une Autre Histoire",
    "errorMessage": "Désolé, il y a eu une erreur lors de la génération de votre histoire. Veuillez réessayer."
  },
  "es-ES": {
    "ageTitle": "Cuéntanos sobre tu hijo",
    "ageSubtitle": "Esto nos ayuda a crear una historia personalizada solo para él/ella",
    "namePlaceholder": "Ingresa el nombre de tu hijo",
    "ageGroupTitle": "Selecciona el grupo de edad:",
    "continueButton": "Continuar",
    "step1of5": "Paso 1 de 5",
    "genderTitle": "Cuéntanos sobre tu hijo",
    "genderSubtitle": "Usaremos los pronombres y perspectiva correctos",
    "boy": "niño",
    "girl": "niña",
    "other": "otro",
    "backButton": "Atrás",
    "step2of5": "Paso 2 de 5",
    "interestsTitle": "¿Qué le encanta a tu hijo?",
    "interestsSubtitle": "Elige 1 cosa que despierte su imaginación",
    "animals": "Animales",
    "spaceStars": "Espacio y Estrellas",
    "oceanSeaLife": "Océano y Vida Marina",
    "dinosaurs": "Dinosaurios",
    "magicFantasy": "Magia y Fantasía",
    "sports": "Deportes",
    "music": "Música",
    "artDrawing": "Arte y Dibujo",
    "natureForest": "Naturaleza y Bosque",
    "superheroes": "Superhéroes",
    "vehiclesTransportation": "Vehículos y Transporte",
    "cookingFood": "Cocina y Comida",
    "scienceExperiments": "Ciencia y Experimentos",
    "step3of5": "Paso 3 de 5",
    "styleTitle": "¿Qué tipo de historia te gustaría?",
    "styleSubtitle": "Elige el tono que mejor se adapte a la hora de dormir",
    "funnyLabel": "Divertida y Tonta",
    "funnyDesc": "Risas y carcajadas en todo momento",
    "adventurousLabel": "Aventurera y Emocionante",
    "adventurousDesc": "Misiones emocionantes y descubrimientos",
    "gentleLabel": "Suave y Tranquilizante",
    "gentleDesc": "Pacífica y relajante",
    "magicalLabel": "Mágica y Encantadora",
    "magicalDesc": "Fantasía y maravilla",
    "educationalLabel": "Educativa y de Aprendizaje",
    "educationalDesc": "Datos divertidos y conocimiento",
    "step4of5": "Paso 4 de 5",
    "lessonTitle": "¿Qué lección deberíamos incluir?",
    "lessonSubtitle": "¿Qué mensaje importante deberían aprender?",
    "lessonPlaceholder": "Ejemplos: Ser amable con otros, probar cosas nuevas, compartir es cuidar, ser valiente cuando tienes miedo...",
    "createStoryButton": "Crear Historia",
    "step5of5": "Paso 5 de 5",
    "generatingTitle": "Creando tu historia mágica...",
    "generatingSubtitle": "Nuestra magia de cuentos está funcionando...",
    "storyReadyTitle": "¡Tu cuento para dormir está listo!",
    "storyReadySubtitle": "¡Los dulces sueños están a solo una historia de distancia!",
    "sweetDreams": "¡Dulces sueños!",
    "createAnotherStory": "Crear Otra Historia",
    "errorMessage": "Lo siento, hubo un error al generar tu historia. Por favor, inténtalo de nuevo."
  }
  /* LOCALE_PLACEHOLDER_END */
};

const locale = 'fr-FR';

const BedtimeStoryGenerator = ({ onStorySaved }) => {
  const { currentUser } = useAuth();
  const { paymentStatus, paymentMessage } = usePayment();
  const { language, setLanguage } = useLanguage();
  const { saveTabState, getTabState } = useNavigation();
  
  const t = (key) => {
    return TRANSLATIONS[language]?.[key] || TRANSLATIONS['en-US'][key] || key;
  };

  // Initialize state from saved navigation state or defaults
  const savedWhizzState = getTabState('WhizZ');
  
  const [currentStep, setCurrentStep] = useState(() => {
    // Only restore state if it's not in loading/generating state
    if (savedWhizzState.currentStep !== undefined && !savedWhizzState.isGenerating) {
      return savedWhizzState.currentStep;
    }
    
    // For Standard users with existing story: Show story directly (no more steps)
    if (savedWhizzState.storyText && savedWhizzState.formData?.name) {
      return 6; // Display story directly
    }
    
    return -1; // Hello page for new users or users without story
  });
  
  const [currentStoryPage, setCurrentStoryPage] = useState(() => {
    return savedWhizzState.currentStoryPage || 0;
  });
  
  const [isGenerating, setIsGenerating] = useState(false); // Always start with false
  const [genProgress, setGenProgress] = useState('starting'); // Live generation progress step
  
  const [storyText, setStoryText] = useState(() => {
    return savedWhizzState.storyText || '';
  });
  
  const [formData, setFormData] = useState(() => {
    return savedWhizzState.formData || {
      name: '',
      age: '',
      gender: '',
      interests: [],
      style: '',
      lesson: ''
    };
  });

  // Payment and quota management
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  
  // Story saved notification
  const [showSavedNotification, setShowSavedNotification] = useState(false);

  // Standard user onboarding and reminder modals
  const [showStandardOnboarding, setShowStandardOnboarding] = useState(false);
  const [showStandardReminder, setShowStandardReminder] = useState(false);
  const [highlightField, setHighlightField] = useState(null);
  const [isParametersExpanded, setIsParametersExpanded] = useState(false);

  // Auto-save navigation state to preserve user progress across tab switches
  useEffect(() => {
    const timer = setTimeout(() => {
      if (currentStep >= -1) {
        const whizzState = {
          currentStep,
          currentStoryPage,
          formData,
          storyText
        };
       
        try {
          saveTabState('WhizZ', whizzState);
        } catch (error) {
          console.warn('Could not save navigation state:', error);
        }
      }
    }, 500);
   
    return () => clearTimeout(timer);
  }, [currentStep]);

  const { 
    subscription, 
    loading: userLoading, 
    refreshUserData,
    hasAvailableStories,
    shouldShowPaymentButton,
    getButtonText,
    getButtonClasses,
    updateQuotaAfterStoryGeneration,
    userData
  } = useUserData();

  // Check if user is Standard subscriber
  const isStandardUser = subscription?.whizz_plan === 'standard';

  // Handle Standard user modal display after story generation
  useEffect(() => {
    if (isStandardUser && storyText && currentStep === 6) {
      const hasSeenOnboarding = localStorage.getItem('standardOnboardingSeen');
      const standardStoriesCount = parseInt(localStorage.getItem('standardStoriesGenerated') || '0');
      
      if (!hasSeenOnboarding && standardStoriesCount === 0) {
        // First time Standard user - Show onboarding tutorial
        setShowStandardOnboarding(true);
        localStorage.setItem('standardStoriesGenerated', '1');
      } else {
        // All other times - Show reminder modal
        setShowStandardReminder(true);
        localStorage.setItem('standardStoriesGenerated', (standardStoriesCount + 1).toString());
      }
    }
  }, [isStandardUser, storyText, currentStep]);

  // Load last story for Standard users on reconnection
  useEffect(() => {
    if (isStandardUser && !storyText && currentStep === -1) {
      // Use lastStory from userData (loaded during authentication)
      const lastStory = userData?.lastStory;
      if (lastStory) {
        // Reconstruct story data structure for display
        const storyData = {
          success: true,
          story_parts: lastStory.content?.story_parts || [],
          visuals: lastStory.content?.visuals || [],
          title: lastStory.content?.title || 'Your Story',
          request: lastStory.originalData?.request || lastStory.storyRequest
        };
        
        setStoryText(storyData);
        
        // Pour Standard users : Ne pas restaurer les paramètres après reconnexion
        // (garder formData vide pour forcer la saisie)
        
        setCurrentStep(6); // Go directly to story display
        setCurrentStoryPage(0); // Start from first page
      }
    }
  }, [isStandardUser, userData, storyText, currentStep]);

  const interests = [
    t('animals'), t('spaceStars'), t('oceanSeaLife'), t('dinosaurs'), t('magicFantasy'),
    t('sports'), t('music'), t('artDrawing'), t('natureForest'), t('superheroes'),
    t('vehiclesTransportation'), t('cookingFood'), t('scienceExperiments')
  ];

  const styles = [
    { value: 'funny', label: t('funnyLabel'), emoji: '😄', desc: t('funnyDesc') },
    { value: 'adventurous', label: t('adventurousLabel'), emoji: '🌟', desc: t('adventurousDesc') },
    { value: 'gentle', label: t('gentleLabel'), emoji: '🌙', desc: t('gentleDesc') },
    { value: 'magical', label: t('magicalLabel'), emoji: '✨', desc: t('magicalDesc') },
    { value: 'educational', label: t('educationalLabel'), emoji: '📚', desc: t('educationalDesc') }
  ];

  const handleInterestToggle = (interest) => {
    setFormData(prev => ({
      ...prev,
      interests: prev.interests.includes(interest)
        ? [] // Désélectionner si déjà sélectionné
        : [interest] // Remplacer par le nouvel intérêt sélectionné
    }));
  };

  const getChapterInfo = (pageIndex, totalPages, age) => {
    // Standards éditoriaux 2025 - Chapitres par tranche d'âge
    if (typeof age === 'string') {
      
      // 3-4 ans (Albums) : Scènes simples ou pas de titre
      if (age.includes('3-4') || age.includes('3') || age.includes('4')) {
        return `Scène ${pageIndex + 1} sur ${totalPages}`;
      } 
      
      // 5-7 ans (Premières lectures) : Numéros simples
      else if (age.includes('5-7') || age.includes('5') || age.includes('6') || age.includes('7')) {
        return `${pageIndex + 1} / ${totalPages}`;
      } 
      
      // 8-10 ans (Romans juniors) : Premiers vrais chapitres (3-4 pages/chapitre)
      else if (age.includes('8-10') || age.includes('8') || age.includes('9') || age.includes('10')) {
        const chapter = Math.ceil((pageIndex + 1) / 4); // 4 pages par chapitre
        const totalChapters = Math.ceil(totalPages / 4);
        return `Chapitre ${chapter} sur ${totalChapters} - Page ${pageIndex + 1}`;
      } 
      
      // 11-13 ans (Romans ados) : Chapitres longs avec parties si >50 pages
      else if (age.includes('11-13') || age.includes('11') || age.includes('12') || age.includes('13')) {
        if (totalPages > 50) {
          // Format "Partie + Chapitre" pour les longs romans
          const pagesPerChapter = 6; // 6 pages par chapitre
          const chaptersPerPart = 5; // 5 chapitres par partie
          
          const chapter = Math.ceil((pageIndex + 1) / pagesPerChapter);
          const part = Math.ceil(chapter / chaptersPerPart);
          const chapterInPart = ((chapter - 1) % chaptersPerPart) + 1;
          
          return `Partie ${part} - Chapitre ${chapterInPart} - Page ${pageIndex + 1}`;
        } else {
          // Format chapitre simple pour romans courts
          const chapter = Math.ceil((pageIndex + 1) / 5); // 5 pages par chapitre
          const totalChapters = Math.ceil(totalPages / 5);
          return `Chapitre ${chapter} sur ${totalChapters} - Page ${pageIndex + 1}`;
        }
      }
    }
    
    // Default fallback
    return `Page ${pageIndex + 1} sur ${totalPages}`;
  };

  // Age-appropriate styling function based on 2025 editorial standards for real books
  const getAgeGroupStyles = (age) => {
    // Determine age group from age string
    if (typeof age === 'string') {
      if (age.includes('3-4') || age.includes('3') || age.includes('4')) {
        return {
          textSize: 'text-lg md:text-xl',         // 18-22pt optimal pour 3-4 ans
          lineHeight: 'leading-relaxed',          // 1.5x optimal pour reconnaissance
          spacing: 'space-y-4 md:space-y-5',     // Espacement modéré
          fontWeight: 'font-bold',                // Maximum readability
          letterSpacing: 'tracking-normal',       // Lettres normales
          maxLines: '5-8',                        // 5-8 lignes max par page
          indent: false                           // Pas d'indentation
        };
      } else if (age.includes('5-7') || age.includes('5') || age.includes('6') || age.includes('7')) {
        return {
          textSize: 'text-lg md:text-xl',         // 16-20pt optimal pour early readers
          lineHeight: 'leading-relaxed',          // Aéré pour faciliter lecture
          spacing: 'space-y-4 md:space-y-5',     // Généreux mais moins qu'avant
          fontWeight: 'font-medium',              // Medium pour early readers
          letterSpacing: 'tracking-normal',
          maxLines: '4-5',                        // 4-5 sentences max par page
          indent: false                           // Paragraphes séparés par espaces
        };
      } else if (age.includes('8-10') || age.includes('8') || age.includes('9') || age.includes('10')) {
        return {
          textSize: 'text-sm md:text-base',        // 12-14pt pour chapter books
          lineHeight: 'leading-normal',            // Espacement standard
          spacing: 'space-y-2 md:space-y-3',      // Moins d'espacement entre paragraphes
          fontWeight: 'font-normal',               // Poids normal pour chapter books
          letterSpacing: 'tracking-normal',
          maxLines: '3-4',                         // 3-4 paragraphes par page
          indent: true                             // Indentation obligatoire
        };
      } else if (age.includes('11-13') || age.includes('11') || age.includes('12') || age.includes('13')) {
        return {
          textSize: 'text-xs md:text-sm',         // 11-13pt pour romans modernes
          lineHeight: 'leading-normal',           // Densité moderne
          spacing: 'space-y-1 md:space-y-2',     // Très compact comme vrais romans
          fontWeight: 'font-normal',
          letterSpacing: 'tracking-normal',
          maxLines: '4-5',                        // 4-5 paragraphes par page
          indent: true                            // Indentation moderne obligatoire
        };
      }
    }
    
    // Default for 5-7 years
    return {
      textSize: 'text-xl md:text-2xl',
      lineHeight: 'leading-relaxed',
      spacing: 'space-y-4 md:space-y-5',
      fontWeight: 'font-semibold',
      letterSpacing: 'tracking-normal',
      maxLines: '8-12',
      indent: false
    };
  };

  // Format text for authentic book display based on age group - Editorial Standards 2025
  const formatStoryText = (text, age) => {
    if (!text || text === "This part of the story is coming soon...") {
      return (
        <div className="text-center py-8 flex-1 flex flex-col justify-center">
          <p className="text-gray-500 italic">Story content loading...</p>
        </div>
      );
    }
    
    // Clean text and split into sentences
    const sentences = text.split(/(?<=[.!?])\s+/).filter(sentence => sentence.trim());
    
    // For 3-4 ans: Large text, simple sentences, centered like picture books
    if (age && (age.includes('3-4') || age.includes('3') || age.includes('4'))) {
      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          padding: '20px',
          paddingBottom: '50px'
        }}>
          
          {/* Zone de texte avec hauteur calculée */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: sentences.length <= 3 ? 'center' : 'space-between',
            flex: 1,
            minHeight: 0
          }}>
            {sentences.map((sentence, index) => (
              <p key={index} style={{
                textAlign: 'center',
                fontFamily: "'Comic Sans MS', 'Arial', sans-serif",
                fontSize: '20px',
                fontWeight: 'bold',
                lineHeight: '1.5',
                color: '#1f2937',
                margin: sentences.length <= 3 ? '10px 0' : '5px 0'
              }}>
                {sentence.trim()}
              </p>
            ))}
          </div>
          
          {/* Numéro de page dans le flow normal */}
          <div style={{
            textAlign: 'right',
            paddingTop: '10px',
            fontSize: '16px',
            fontWeight: 'bold',
            color: '#6366f1',
            fontFamily: "'Comic Sans MS', sans-serif"
          }}>
            Page {currentStoryPage + 1}
          </div>
        </div>
      );
    }
    
    // For 5-7 ans: Early readers format with intelligent distribution
    if (age && (age.includes('5-7') || age.includes('5') || age.includes('6') || age.includes('7'))) {
      // Limit to max 5 sentences per page for early readers
      const pageContent = sentences.slice(0, 5);
      
      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          padding: '20px',
          paddingBottom: '50px'
        }}>
          
          {/* Zone de texte avec hauteur calculée */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: pageContent.length <= 4 ? 'center' : 'space-between',
            flex: 1,
            minHeight: 0
          }}>
            {pageContent.map((sentence, index) => (
              <p key={index} style={{
                textAlign: 'left',
                fontFamily: "'Arial', 'Comic Sans MS', sans-serif",
                fontSize: '18px',
                fontWeight: '600',
                lineHeight: '1.4',
                color: '#1f2937',
                margin: pageContent.length <= 4 ? '8px 0' : '4px 0'
              }}>
                {sentence.trim()}
              </p>
            ))}
          </div>
          
          {/* Numéro de page dans le flow normal */}
          <div style={{
            textAlign: 'right',
            paddingTop: '10px',
            fontSize: '16px',
            fontWeight: 'bold',
            color: '#6366f1',
            fontFamily: "'Arial', sans-serif"
          }}>
            Page {currentStoryPage + 1}
          </div>
        </div>
      );
    }
    
    // For 8-10 ans: Natural book-like text display
    if (age && (age.includes('8-10') || age.includes('8') || age.includes('9') || age.includes('10'))) {
      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          padding: '20px',
          paddingBottom: '50px'
        }}>
          
          {/* Zone de texte avec hauteur calculée */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            flex: 1,
            minHeight: 0
          }}>
            <div style={{
              textAlign: 'left',
              fontFamily: "'Georgia', 'Times New Roman', serif",
              fontSize: '16px',
              fontWeight: '400',
              lineHeight: '1.8',
              color: '#1f2937',
              hyphens: 'auto',
              textIndent: '1.5em',
              letterSpacing: '0.3px'
            }}>
              {text.trim()}
            </div>
          </div>
          
          {/* Numéro de page dans le flow normal */}
          <div style={{
            textAlign: 'right',
            paddingTop: '10px',
            fontSize: '16px',
            fontWeight: 'bold',
            color: '#6366f1',
            fontFamily: "'Georgia', serif"
          }}>
            Page {currentStoryPage + 1}
          </div>
        </div>
      );
    }
    
    // For 11-13 ans: Natural book-like text display  
    if (age && (age.includes('11-13') || age.includes('11') || age.includes('12') || age.includes('13'))) {
      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          padding: '20px',
          paddingBottom: '50px'
        }}>
          
          {/* Zone de texte avec hauteur calculée */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            flex: 1,
            minHeight: 0
          }}>
            <div style={{
              textAlign: 'left',
              fontFamily: "'Georgia', 'Times New Roman', serif",
              fontSize: '16px',
              fontWeight: '400',
              lineHeight: '1.8',
              color: '#1f2937',
              hyphens: 'auto',
              textIndent: '1.5em',
              letterSpacing: '0.3px'
            }}>
              {text.trim()}
            </div>
          </div>
          
          {/* Numéro de page dans le flow normal */}
          <div style={{
            textAlign: 'right',
            paddingTop: '10px',
            fontSize: '16px',
            fontWeight: 'bold',
            color: '#6366f1',
            fontFamily: "'Georgia', serif"
          }}>
            Page {currentStoryPage + 1}
          </div>
        </div>
      );
    }
    
    // Default: Fallback should never be reached with proper age detection
    return (
      <div className="p-4">
        <p className="text-gray-800 text-left font-serif text-base leading-relaxed">
          {text.trim()}
        </p>
      </div>
    );
  };

  const nextStep = () => {
    setCurrentStep(currentStep + 1);
  };

  const prevStep = () => {
    setCurrentStep(currentStep - 1);
  };

  const pollGenerationJob = async (jobId) => {
    // Poll every 3s for up to 6 minutes
    for (let attempt = 0; attempt < 120; attempt++) {
      await new Promise(resolve => setTimeout(resolve, 3000));
      const { data: job } = await apiService.bedtime.getJobStatus(jobId);
      if (job.progress) setGenProgress(job.progress);
      if (job.status === 'done') return job.result;
      if (job.status === 'error') return { success: false, error: job.error };
    }
    return { success: false, error: t('errorMessage') };
  };

  const generateStory = async () => {
    // Check if user has available stories before proceeding
    if (shouldShowPaymentButton) {
      setShowPaymentModal(true);
      return;
    }

    setIsGenerating(true);
    setGenProgress('starting');
    setCurrentStep(5);

    try {
      // Prepare the data for the API
      const storyData = {
        name: formData.name,
        age: formData.age,
        gender: formData.gender,
        interests: formData.interests,
        style: formData.style,
        lesson: formData.lesson,
        language: language // Utiliser la langue sélectionnée dans LanguageContext
      };

      // Call the backend API (starts a background job, then poll until done)
      const response = await apiService.bedtime.generateStory(storyData);
      let resultData = response.data;
      if (resultData.job_id) {
        resultData = await pollGenerationJob(resultData.job_id);
      }
      
      if (resultData.success) {
        console.log("✅ Backend response received:", resultData);
        
        // Store the complete story data structure
        const completeStoryData = {
          ...resultData,
          request: storyData // Save the original request data
        };
        
        console.log("✅ Complete story data prepared:", completeStoryData);
        setStoryText(completeStoryData);
        console.log("✅ Story text set successfully");
        
        setCurrentStoryPage(0); // Start from first page
        
        // Save the story to My Stories
        try {
          console.log("📱 Attempting localStorage save...");
          // First save to localStorage for immediate feedback (legacy support)
          saveStoryToMyStories(completeStoryData);
          console.log('✅ Story saved to localStorage for immediate access');
          
          // Show saved notification popup immediately
          setShowSavedNotification(true);
          setTimeout(() => setShowSavedNotification(false), 6000);
          
        } catch (saveError) {
          console.warn('⚠️ Could not save story to localStorage:', saveError);
          
          // Still show notification even if localStorage fails since backend saved it
          setShowSavedNotification(true);
          setTimeout(() => setShowSavedNotification(false), 6000);
        }
        
        setCurrentStep(6);
        console.log("✅ Moving to step 6");
        
        // Pour Standard users : Vider formData après génération réussie
        if (isStandardUser) {
          setFormData({
            name: '',
            age: '',
            gender: '',
            interests: [],
            style: '',
            lesson: ''
          });
        }
        
        // Force refresh user data to update quota display
        console.log("🔄 Updating quota...");
        await updateQuotaAfterStoryGeneration();
        console.log("✅ Quota updated successfully");
        
        // Trigger notification badge in parent component after all processing is complete
        if (onStorySaved) {
          onStorySaved();
        }
      } else {
        console.error('Story generation failed:', resultData.error);
        if (resultData.error?.toLowerCase().includes('quota') || resultData.error?.toLowerCase().includes('payment')) {
          setShowPaymentModal(true);
          setCurrentStep(4); // Go back to lesson step
        } else {
          alert(resultData.error || t('errorMessage'));
          setCurrentStep(4);
        }
      }
      
    } catch (error) {
      console.error('Error generating story:', error);
      
      // Extract error message from response if available
      const errorMessage = error.response?.data?.detail || 
                          error.response?.data?.error || 
                          error.message;
      
      // Check if it's a quota/payment related error
      if (errorMessage?.includes('quota') || errorMessage?.includes('payment') || errorMessage?.includes('exceeded') || error.response?.status === 400) {
        // Force refresh user data to get current quota status
        try {
          console.log('Refreshing user data after quota error...');
          refreshUserData();
          console.log('User data refreshed successfully');
          setShowPaymentModal(true);
          setCurrentStep(4);
        } catch (refreshError) {
          console.error('Error refreshing user data:', refreshError);
          setCurrentStep(4);
        }
      } else {
        try {
          console.log('Showing error message and resetting step...');
          alert(errorMessage || t('errorMessage'));
          setCurrentStep(4);
          console.log('Error handling completed');
        } catch (alertError) {
          console.error('Error showing alert:', alertError);
          setCurrentStep(4);
        }
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const resetForm = () => {
    setCurrentStep(-1); // Return to welcome page
    setCurrentStoryPage(0); // Reset story page navigation
    setStoryText('');
    setIsGenerating(false);
    setFormData({
      name: '',
      age: '',
      gender: '',
      interests: [],
      style: '',
      lesson: '',
      format: 'short'
    });
    // Refresh user status when resetting
    refreshUserData();
  };

  // Handle parameter updates from StoryParametersBanner
  const handleParameterUpdate = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle click on story creation buttons - check quota first
  const handleStoryButtonClick = () => {
    if (shouldShowPaymentButton) {
      setShowPaymentModal(true);
    } else {
      // Start story creation process
      if (currentStep === -1) {
        setCurrentStep(0); // Go to age step
      } else if (currentStep === 4) {
        generateStory(); // Generate story from lesson step
      } else if (currentStep === 6) {
        // "Create Another Story" logic
        handleCreateAnotherStory();
      }
    }
  };

  const handleCreateAnotherStory = () => {
    if (isStandardUser) {
      // Vérifier que tous les champs obligatoires sont remplis
      const isFormComplete = formData.name?.trim() && 
                            formData.age && 
                            formData.gender && 
                            formData.interests?.length > 0 && 
                            formData.style && 
                            formData.lesson?.trim();
      
      if (isFormComplete) {
        setCurrentStep(5); // Go directly to generation step
        generateStory();
      }
      // Si pas complet : ne rien faire, l'utilisateur doit compléter les paramètres
    } else {
      // Free/On-demand users: Reset to steps
      resetForm();
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 0: return formData.name.trim() !== '';
      case 1: return formData.age !== '';
      case 2: return formData.gender !== '';
      case 3: return formData.interests.length > 0;
      case 4: return formData.style !== '' && formData.lesson.trim() !== '';
      default: return false;
    }
  };

  // Story navigation functions
  const nextStoryPage = () => {
    if (storyText && storyText.story_parts) {
      setCurrentStoryPage(prev => Math.min(prev + 1, storyText.story_parts.length - 1));
    } else {
      setCurrentStoryPage(prev => Math.min(prev + 1, 3)); // Fallback to old behavior
    }
  };

  const prevStoryPage = () => {
    setCurrentStoryPage(prev => Math.max(prev - 1, 0));
  };

  // Generate story title based on AI response
  const getStoryTitle = () => {
    // Use the title generated by AI
    if (storyText && storyText.title) {
      return storyText.title;
    }
    return 'Your Bedtime Story';
  };

  const getStepTitle = () => {
    const titles = [
      t('ageTitle'),
      t('genderTitle'),
      t('interestsTitle'),
      t('styleTitle'),
      t('lessonTitle'),
      t('generatingTitle'),
      t('storyReadyTitle')
    ];
    return titles[currentStep];
  };

  const getStepSubtitle = () => {
    const subtitles = [
      t('ageSubtitle'),
      t('genderSubtitle'),
      t('interestsSubtitle'),
      t('styleSubtitle'),
      t('lessonSubtitle'),
      t('generatingSubtitle'),
      t('storyReadySubtitle')
    ];
    return subtitles[currentStep];
  };

  // Welcome page
  if (currentStep === -1) {
    return (
      <>
        <WelcomePage 
          onStartStory={handleStoryButtonClick}
          buttonText={getButtonText(t('createStoryButton'))}
          buttonClasses={getButtonClasses("bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white px-8 py-3 rounded-full text-base font-bold transition-all duration-200 transform hover:scale-105 shadow-lg")}
        />
        <PaymentModal
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          onSuccess={() => {
            setShowPaymentModal(false);
            refreshUserData();
          }}
        />
      </>
    );
  }

  // Step 0: Name and Age Group
  if (currentStep === 0) {
    const ageGroups = ['3-4', '5-7', '8-10', '11-13'];
    
    return (
      <div className="h-full bg-gradient-to-br from-indigo-100 via-purple-50 to-pink-100 overflow-y-auto">
        <div className="flex items-center justify-center p-4 pb-32 md:pb-24 min-h-full">
          <div className="bg-white rounded-3xl shadow-2xl p-6 max-w-md w-full">
          <div className="text-center mb-6">
            <div className="flex justify-center mb-3">
              <Sparkles className="w-10 h-10 text-indigo-500" />
            </div>
            <h1 className="text-2xl font-bold text-gray-800 mb-2">{getStepTitle()}</h1>
            <p className="text-gray-600 text-sm">{getStepSubtitle()}</p>
          </div>

          <div className="space-y-4">
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              placeholder={t('namePlaceholder')}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:outline-none transition-colors text-lg text-center font-medium"
              autoFocus
            />

            <div className="mt-4">
              <p className="text-sm font-medium text-gray-700 mb-2 text-center">{t('ageGroupTitle')}</p>
              <div className="grid grid-cols-2 gap-2">
                {ageGroups.map((ageGroup) => (
                  <button
                    key={ageGroup}
                    onClick={() => setFormData({...formData, age: ageGroup})}
                    className={`py-2.5 px-3 rounded-lg border-2 transition-all duration-200 text-center font-bold text-sm ${
                      formData.age === ageGroup
                        ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                        : 'border-gray-200 hover:border-gray-300 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {ageGroup} ans
                  </button>
                ))}
              </div>
            </div>

            <button
              onClick={nextStep}
              disabled={!canProceed()}
              className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white py-3 px-6 rounded-xl font-bold text-base transition-all duration-200 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
            >
              {t('continueButton')}
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          <div className="mt-6 text-center">
            <div className="text-sm text-gray-500">{t('step1of5')}</div>
            <div className="w-full bg-gray-200 rounded-full h-1.5 mt-2">
              <div className="bg-indigo-500 h-1.5 rounded-full transition-all duration-300" style={{width: '20%'}}></div>
            </div>
          </div>
        </div>
        </div>
      </div>
    );
  }

  // Step 1: Gender
  if (currentStep === 1) {
    return (
      <div className="h-full bg-gradient-to-br from-indigo-100 via-purple-50 to-pink-100 overflow-y-auto">
        <div className="flex items-center justify-center p-4 pb-32 md:pb-24 min-h-full">
          <div className="bg-white rounded-3xl shadow-2xl p-6 max-w-md w-full">
          <div className="text-center mb-6">
            <div className="flex justify-center mb-3">
              <Heart className="w-10 h-10 text-purple-500" />
            </div>
            <h1 className="text-2xl font-bold text-gray-800 mb-2">{getStepTitle()}</h1>
            <p className="text-gray-600 text-sm">{getStepSubtitle()}</p>
          </div>

          <div className="space-y-3">
            {['boy', 'girl', 'other'].map((gender) => (
              <button
                key={gender}
                onClick={() => setFormData({...formData, gender})}
                className={`w-full py-3 px-4 rounded-xl border-2 transition-all duration-200 text-center capitalize font-bold text-base ${
                  formData.gender === gender
                    ? 'border-purple-500 bg-purple-50 text-purple-700'
                    : 'border-gray-200 hover:border-gray-300 text-gray-600 hover:bg-gray-50'
                }`}
              >
                {t(gender)}
              </button>
            ))}
          </div>

          <div className="flex gap-3 mt-6">
            <button
              onClick={prevStep}
              className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-2.5 px-4 rounded-xl font-medium transition-all duration-200 flex items-center justify-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              {t('backButton')}
            </button>
            <button
              onClick={nextStep}
              disabled={!canProceed()}
              className="flex-2 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white py-2.5 px-4 rounded-xl font-bold transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {t('continueButton')}
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          <div className="mt-6 text-center">
            <div className="text-sm text-gray-500">{t('step2of5')}</div>
            <div className="w-full bg-gray-200 rounded-full h-1.5 mt-2">
              <div className="bg-indigo-500 h-1.5 rounded-full transition-all duration-300" style={{width: '40%'}}></div>
            </div>
          </div>
        </div>
        </div>
      </div>
    );
  }

  // Step 2: Interests
  if (currentStep === 2) {
    return (
      <div className="h-full bg-gradient-to-br from-indigo-100 via-purple-50 to-pink-100 overflow-y-auto">
        <div className="flex items-center justify-center p-4 pb-32 md:pb-24 min-h-full">
          <div className="bg-white rounded-3xl shadow-2xl p-6 max-w-lg w-full">
          <div className="text-center mb-6">
            <div className="flex justify-center mb-3">
              <Star className="w-10 h-10 text-pink-500" />
            </div>
            <h1 className="text-xl lg:text-2xl font-bold text-gray-800 mb-2">{getStepTitle()}</h1>
            <p className="text-gray-600 text-sm">{getStepSubtitle()}</p>
          </div>

          <div className="grid grid-cols-2 gap-2 mb-6">
            {interests.map((interest) => (
              <button
                key={interest}
                onClick={() => handleInterestToggle(interest)}
                className={`py-2.5 px-3 rounded-lg border-2 transition-all duration-200 text-center font-medium text-xs ${
                  formData.interests.includes(interest)
                    ? 'border-pink-500 bg-pink-50 text-pink-700'
                    : 'border-gray-200 hover:border-gray-300 text-gray-600 hover:bg-gray-50'
                }`}
              >
                {interest}
              </button>
            ))}
          </div>

          <div className="flex gap-3">
            <button
              onClick={prevStep}
              className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-2.5 px-4 rounded-xl font-medium transition-all duration-200 flex items-center justify-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              {t('backButton')}
            </button>
            <button
              onClick={nextStep}
              disabled={!canProceed()}
              className="flex-2 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white py-2.5 px-4 rounded-xl font-bold transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {t('continueButton')}
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          <div className="mt-6 text-center">
            <div className="text-sm text-gray-500">{t('step3of5')}</div>
            <div className="w-full bg-gray-200 rounded-full h-1.5 mt-2">
              <div className="bg-indigo-500 h-1.5 rounded-full transition-all duration-300" style={{width: '60%'}}></div>
            </div>
          </div>
        </div>
        </div>
      </div>
    );
  }

  // Step 3: Style
  if (currentStep === 3) {
    return (
      <div className="h-full bg-gradient-to-br from-indigo-100 via-purple-50 to-pink-100 overflow-y-auto">
        <div className="flex items-center justify-center p-4 pb-32 md:pb-24 min-h-full">
          <div className="bg-white rounded-3xl shadow-2xl p-6 max-w-md w-full">
          <div className="text-center mb-6">
            <div className="flex justify-center mb-3">
              <Book className="w-10 h-10 text-orange-500" />
            </div>
            <h1 className="text-xl lg:text-2xl font-bold text-gray-800 mb-2">{getStepTitle()}</h1>
            <p className="text-gray-600 text-sm">{getStepSubtitle()}</p>
          </div>

          <div className="space-y-2 mb-6">
            {styles.map((style) => (
              <button
                key={style.value}
                onClick={() => setFormData({...formData, style: style.value})}
                className={`w-full py-3 px-4 rounded-xl border-2 transition-all duration-200 text-left font-medium flex items-center gap-3 ${
                  formData.style === style.value
                    ? 'border-orange-500 bg-orange-50 text-orange-700'
                    : 'border-gray-200 hover:border-gray-300 text-gray-600 hover:bg-gray-50'
                }`}
              >
                <span className="text-2xl">{style.emoji}</span>
                <div>
                  <div className="font-bold text-sm">{style.label}</div>
                  <div className="text-xs opacity-70">{style.desc}</div>
                </div>
              </button>
            ))}
          </div>

          <div className="flex gap-3">
            <button
              onClick={prevStep}
              className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-2.5 px-4 rounded-xl font-medium transition-all duration-200 flex items-center justify-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              {t('backButton')}
            </button>
            <button
              onClick={nextStep}
              disabled={!canProceed()}
              className="flex-2 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white py-2.5 px-4 rounded-xl font-bold transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {t('continueButton')}
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          <div className="mt-6 text-center">
            <div className="text-sm text-gray-500">{t('step4of5')}</div>
            <div className="w-full bg-gray-200 rounded-full h-1.5 mt-2">
              <div className="bg-indigo-500 h-1.5 rounded-full transition-all duration-300" style={{width: '80%'}}></div>
            </div>
          </div>
        </div>
        </div>
      </div>
    );
  }

  // Step 4: Lesson
  if (currentStep === 4) {
    return (
      <div className="h-full bg-gradient-to-br from-indigo-100 via-purple-50 to-pink-100 overflow-y-auto">
        <div className="flex items-center justify-center p-4 pb-32 md:pb-24 min-h-full">
          <div className="bg-white rounded-3xl shadow-2xl p-6 max-w-md w-full">
          <div className="text-center mb-6">
            <div className="flex justify-center mb-3">
              <Heart className="w-10 h-10 text-green-500" />
            </div>
            <h1 className="text-xl lg:text-2xl font-bold text-gray-800 mb-2">{getStepTitle()}</h1>
            <p className="text-gray-600 text-sm">{getStepSubtitle()}</p>
          </div>

          <div className="space-y-4">
            <textarea
              value={formData.lesson}
              onChange={(e) => setFormData({...formData, lesson: e.target.value})}
              placeholder={t('lessonPlaceholder')}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-green-500 focus:outline-none transition-colors resize-none h-24 text-base"
              autoFocus
            />
          </div>

          <div className="flex gap-3">
              <button
                onClick={prevStep}
                className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-2.5 px-4 rounded-xl font-medium transition-all duration-200 flex items-center justify-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                {t('backButton')}
              </button>
              <button
                onClick={handleStoryButtonClick}
                disabled={!canProceed()}
                className={getButtonClasses("flex-2 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white py-2.5 px-4 rounded-xl font-bold transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2")}
              >
                {getButtonText(t('createStoryButton'))}
                <Sparkles className="w-4 h-4" />
              </button>
            </div>

          <div className="mt-6 text-center">
            <div className="text-sm text-gray-500">{t('step5of5')}</div>
            <div className="w-full bg-gray-200 rounded-full h-1.5 mt-2">
              <div className="bg-indigo-500 h-1.5 rounded-full transition-all duration-300" style={{width: '100%'}}></div>
            </div>
          </div>
        </div>
        </div>
      </div>
    );
  }

  // Step 5: Loading
  if (currentStep === 5) {
    const isFrUI = language === 'fr-FR';
    const genSteps = [
      { key: 'writing', icon: Feather, label: isFrUI ? "Écriture de l'histoire" : 'Writing the story', sub: isFrUI ? 'Notre conteur imagine chaque page...' : 'Our storyteller is dreaming up every page...' },
      { key: 'illustrating', icon: Palette, label: isFrUI ? "Création de l'illustration" : 'Painting the illustration', sub: isFrUI ? "Un coup de pinceau magique..." : 'A stroke of magic paint...' },
      { key: 'saving', icon: Wand2, label: isFrUI ? 'Touches finales' : 'Final magic touches', sub: isFrUI ? 'On range tout dans votre bibliothèque...' : 'Tucking it into your library...' }
    ];
    const stepOrder = { starting: 0, writing: 0, illustrating: 1, saving: 2 };
    const activeIdx = stepOrder[genProgress] ?? 0;
    const progressPct = [12, 55, 88][activeIdx];

    return (
      <div className="h-full bg-gradient-to-br from-indigo-100 via-purple-50 to-pink-100 overflow-y-auto">
        <style>{`
          @keyframes gen-float { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-6px); } }
          @keyframes gen-glow { 0%,100% { opacity: .35; } 50% { opacity: .7; } }
          @keyframes gen-shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
          .gen-moon { animation: gen-float 3s ease-in-out infinite; }
          .gen-aura { animation: gen-glow 2.4s ease-in-out infinite; }
          .gen-bar { transition: width 1s cubic-bezier(.4,0,.2,1); }
          .gen-active-label { background: linear-gradient(90deg, #7c3aed, #ec4899, #7c3aed); background-size: 200% auto; -webkit-background-clip: text; background-clip: text; color: transparent; animation: gen-shimmer 2.5s linear infinite; }
        `}</style>
        <div className="flex items-center justify-center p-4 pb-32 md:pb-24 min-h-full">
          <div className="relative bg-white/90 backdrop-blur-xl rounded-[2rem] shadow-2xl p-8 max-w-md w-full overflow-hidden" data-testid="generation-progress-card">
            {/* Aura décorative */}
            <div className="gen-aura absolute -top-16 -right-16 w-48 h-48 bg-gradient-to-br from-purple-300 to-pink-300 rounded-full blur-3xl pointer-events-none" />
            <div className="gen-aura absolute -bottom-16 -left-16 w-40 h-40 bg-gradient-to-tr from-indigo-300 to-purple-200 rounded-full blur-3xl pointer-events-none" style={{ animationDelay: '1.2s' }} />

            <div className="relative text-center mb-7">
              <div className="gen-moon inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 shadow-lg shadow-purple-300/50 mb-4">
                <Moon className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800">{getStepTitle()}</h2>
            </div>

            {/* Barre de progression */}
            <div className="relative h-2 bg-purple-100 rounded-full mb-8 overflow-hidden" data-testid="generation-progress-bar">
              <div className="gen-bar h-full rounded-full bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500" style={{ width: `${progressPct}%` }} />
            </div>

            {/* Étapes en temps réel */}
            <div className="relative space-y-4" data-testid="generation-steps">
              {genSteps.map((s, i) => {
                const Icon = s.icon;
                const isDone = i < activeIdx;
                const isActive = i === activeIdx;
                return (
                  <div key={s.key} data-testid={`generation-step-${s.key}`}
                    className={`flex items-center gap-4 p-3.5 rounded-2xl transition-all duration-500 ${
                      isActive ? 'bg-gradient-to-r from-purple-50 to-pink-50 ring-1 ring-purple-200 shadow-sm' : ''
                    } ${!isDone && !isActive ? 'opacity-40' : ''}`}>
                    <div className={`relative flex items-center justify-center w-11 h-11 rounded-xl flex-shrink-0 transition-all duration-500 ${
                      isDone ? 'bg-gradient-to-br from-emerald-400 to-teal-500 shadow-md shadow-emerald-200'
                      : isActive ? 'bg-gradient-to-br from-purple-500 to-pink-500 shadow-md shadow-purple-200'
                      : 'bg-gray-100'
                    }`}>
                      {isDone ? <Check className="w-5 h-5 text-white" />
                        : <Icon className={`w-5 h-5 ${isActive ? 'text-white' : 'text-gray-400'}`} />}
                      {isActive && <span className="absolute inset-0 rounded-xl ring-2 ring-purple-400/60 animate-ping" />}
                    </div>
                    <div className="text-left min-w-0">
                      <p className={`text-sm font-bold ${isActive ? 'gen-active-label' : isDone ? 'text-gray-700' : 'text-gray-400'}`}>
                        {s.label}
                      </p>
                      {isActive && <p className="text-xs text-gray-500 mt-0.5">{s.sub}</p>}
                    </div>
                    {isActive && <Loader2 className="w-4 h-4 text-purple-400 animate-spin ml-auto flex-shrink-0" />}
                  </div>
                );
              })}
            </div>

            <p className="relative text-center text-xs text-gray-400 mt-7">
              {isFrUI ? '✨ Environ 30 secondes de patience magique' : '✨ About 30 seconds of magical patience'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Step 6: Story Display with Navigation
  if (currentStep === 6) {
    // Extract story parts and visuals from backend response
    let storyParts = [];
    let visuals = [];
    let hasRealData = false;
    
    console.log("🔍 DEBUG - Received storyText:", storyText);
    
    // Check if we have real generated data
    if (storyText && storyText.story_parts && storyText.story_parts.length > 0) {
      storyParts = storyText.story_parts;
      visuals = storyText.visuals || [];
      hasRealData = true;
      console.log("✅ Found real story parts:", storyParts.length);
      console.log("✅ Found visuals:", visuals.length);
    } else if (storyText && storyText.story) {
      // Fallback: Use single story content
      console.log("⚠️ Using fallback - single story content");
      storyParts = [storyText.story];
      visuals = storyText.visuals || [];
      hasRealData = storyParts.length > 0 && storyParts[0] !== "This part of the story is coming soon...";
    }
    
    // Only use placeholders if we have no real data at all
    if (!hasRealData || storyParts.length === 0) {
      console.log("❌ No real data found - using placeholders");
      storyParts = [
        "This part of the story is coming soon...",
        "This part of the story is coming soon...", 
        "This part of the story is coming soon...",
        "This part of the story is coming soon..."
      ];
      visuals = [];
    }
    
    // Use actual number of story parts (no artificial minimum)
    const currentPart = storyParts[currentStoryPage] || "This part of the story is coming soon...";
    const currentVisual = visuals?.[0]; // Use first image for all pages
    const totalParts = storyParts.length;

    // Story part titles for narrative structure (in French for consistency)
    const storyPartTitles = [
      "Situation initiale", // Initial situation
      "Élément perturbateur", // Disrupting element
      "Péripéties", // Adventures/complications
      "Dénouement" // Resolution
    ];

    console.log("DEBUG Story Display:", {
      hasRealData,
      totalParts,
      currentStoryPage,
      currentPart: currentPart ? "EXISTS" : "NULL",
      currentVisual: currentVisual ? "EXISTS" : "NULL",
      storyPartsLength: storyParts.length,
      visualsLength: visuals.length,
      storyText: storyText ? "EXISTS" : "NULL"
    });

    // Helper pour détecter si Standard user avec paramètres vides
    const isStandardUserWithEmptyParams = isStandardUser && 
      (!formData.name?.trim() || !formData.age || !formData.gender || 
       !formData.interests?.length || !formData.style || !formData.lesson?.trim());

    return (
      <div className="h-full bg-gradient-to-br from-indigo-100 via-purple-50 to-pink-100 overflow-y-auto">
        <div className="p-4 pb-32 md:pb-24 min-h-full">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
            {/* Header with Story Parameters Title and Language Selector - Standard users only */}
            {isStandardUser && (
              <div className="bg-gradient-to-r from-indigo-500 to-purple-600 p-4 flex-shrink-0">
                <div className="flex items-center justify-between">
                  <div className="flex-1"></div>
                  <div className="text-center">
                    <h1 className="text-2xl font-bold text-white mb-1">
                      {language === 'fr-FR' ? 'Consignes narratives' : 'Narrative Guidelines'}
                    </h1>
                  </div>
                  <div className="flex-1"></div>
                </div>
              </div>
            )}
            
            {/* Story Parameters Banner - Standard users only */}
            {isStandardUser && (
              <StoryParametersBanner 
                parameters={formData}
                onParameterUpdate={handleParameterUpdate}
                editable={isStandardUser}
                highlightField={highlightField}
                onExpandChange={setIsParametersExpanded}
                className={(isStandardUserWithEmptyParams && !isParametersExpanded) ? "animate-pulse border-2 border-indigo-400" : ""}
              />
            )}
            
            {/* Image Banner - Full Width Matching Text Content */}
            <div className="p-4">
              {(currentVisual?.url && !isStandardUser) ? (
                <img 
                  src={currentVisual.url} 
                  alt={`${storyPartTitles[currentStoryPage]} - Story Illustration`}
                  className="w-full rounded-xl shadow-lg block"
                  style={{ 
                    height: 'auto',
                    minHeight: '200px',
                    objectFit: 'cover',
                    display: 'block'
                  }}
                  onError={(e) => {
                    console.log("Image failed to load:", currentVisual.url);
                    e.target.style.display = 'none';
                    const fallbackDiv = e.target.nextElementSibling;
                    if (fallbackDiv) fallbackDiv.style.display = 'flex';
                  }}
                />
              ) : null}
              
              <div 
                className="w-full h-64 bg-gray-100 rounded-2xl shadow-lg border border-gray-200 relative overflow-hidden"
                style={{ display: (currentVisual?.url && !isStandardUser) ? 'none' : 'block' }}
              >
                {/* Central content */}
                <div className="relative z-10 flex flex-col items-center justify-center h-full text-center p-6">
                  <div className="mb-4">
                    <div className="w-16 h-16 bg-gray-300 rounded-2xl flex items-center justify-center">
                      <Book className="w-8 h-8 text-gray-500" />
                    </div>
                  </div>
                  
                  <h3 className="text-lg font-medium text-gray-600 mb-2">
                    Story Image
                  </h3>
                  <p className="text-sm text-gray-500 leading-relaxed max-w-xs">
                    Complete your story parameters to generate your illustration
                  </p>
                </div>
              </div>
            </div>
            
            {/* Story Content - HAUTEUR FIXE FORMAT LIVRE */}
            <div className="p-4">
              <div 
                className="bg-gradient-to-br from-violet-50 via-purple-25 to-indigo-50 rounded-2xl p-8 border-l-6 border-white shadow-inner"
                style={{
                  height: window.innerWidth < 768 ? '400px' : '600px',
                  overflowY: window.innerWidth < 768 ? 'auto' : 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                  backgroundColor: (storyText?.request?.age || formData.age) && ((storyText?.request?.age || formData.age).includes('3-4') || (storyText?.request?.age || formData.age).includes('3') || (storyText?.request?.age || formData.age).includes('4')) ? '#fef7ff' : undefined,
                  border: (storyText?.request?.age || formData.age) && ((storyText?.request?.age || formData.age).includes('3-4') || (storyText?.request?.age || formData.age).includes('3') || (storyText?.request?.age || formData.age).includes('4')) ? '2px solid #e5e7eb' : undefined
                }}
              >
                {(currentPart !== "This part of the story is coming soon..." && !isStandardUser) ? (
                  <div className="flex-1">
                    {/* Format livre par âge avec formatage intelligent */}
                    {formatStoryText(currentPart, storyText?.request?.age || formData.age)}
                  </div>
                ) : (
                  <div className="flex-1 flex flex-col items-center justify-center p-8">
                    {/* Main content */}
                    <div className="text-center max-w-md">
                      <div className="mb-6">
                        <div className="w-20 h-20 bg-gray-300 rounded-full flex items-center justify-center mx-auto">
                          <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center">
                            <Book className="w-8 h-8 text-gray-500" />
                          </div>
                        </div>
                      </div>
                      
                      <h3 className="text-xl font-medium text-gray-600 mb-3">
                        Story Content
                      </h3>
                      
                      <p className="text-gray-500 text-sm leading-relaxed mb-6">
                        Complete the story parameters above to generate your personalized bedtime story
                      </p>
                      
                      <div className="inline-flex items-center px-4 py-2 bg-gray-100 rounded-full border border-gray-200">
                        <span className="text-gray-600 text-xs font-medium">Waiting for parameters</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Navigation Footer */}
            <div className="bg-white border-t border-gray-200 p-6">
              <div className="flex justify-between items-center mb-6">
                <button
                  onClick={prevStoryPage}
                  disabled={currentStoryPage === 0}
                  className="flex items-center gap-2 text-purple-500 hover:text-purple-700 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors duration-200 font-medium text-base"
                >
                  <ArrowLeft className="w-5 h-5" />
                  Previous
                </button>
                
                <div className="text-center">
                  <span className="text-gray-500 text-sm">
                    Page {currentStoryPage + 1} of {totalParts}
                  </span>
                </div>
                
                <button
                  onClick={nextStoryPage}
                  disabled={currentStoryPage === totalParts - 1 || isStandardUserWithEmptyParams}
                  className="flex items-center gap-2 text-purple-500 hover:text-purple-700 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors duration-200 font-medium text-base"
                >
                  Next
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>

              <button
                onClick={handleStoryButtonClick}
                disabled={isStandardUserWithEmptyParams}
                className={`w-full py-4 px-6 rounded-2xl font-bold text-lg transition-all duration-200 transform hover:scale-[1.02] flex items-center justify-center gap-3 shadow-lg hover:shadow-xl ${
                  isStandardUserWithEmptyParams 
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                    : 'bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white'
                }`}
              >
                <Sparkles className="w-6 h-6" />
                Create Another Story
                <Heart className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>
        
        {/* Story Saved Notification Popup */}
        {showSavedNotification && (
          <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
            <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-sm mx-4 transform scale-100 transition-all duration-300">
              <div className="text-center">
                {/* Close button */}
                <button
                  onClick={() => setShowSavedNotification(false)}
                  className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
                
                {/* Success icon */}
                <div className="mb-6">
                  <div className="w-20 h-20 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full flex items-center justify-center mx-auto shadow-lg">
                    <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                </div>
                
                {/* Title */}
                <h3 className="text-2xl font-bold text-gray-800 mb-3">
                  {language === 'fr-FR' ? 'Histoire sauvegardée !' : 'Story Saved!'}
                </h3>
                
                {/* Message */}
                <p className="text-gray-600 text-base leading-relaxed mb-6">
                  {language === 'fr-FR' 
                    ? 'Votre histoire a été automatiquement sauvegardée dans "My Stories". Vous pouvez la retrouver à tout moment !' 
                    : 'Your story has been automatically saved in "My Stories". You can find it anytime!'
                  }
                </p>
                
                {/* Action button */}
                <button
                  onClick={() => setShowSavedNotification(false)}
                  className="w-full bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white py-3 px-6 rounded-2xl font-semibold text-base transition-all duration-200 transform hover:scale-105 shadow-lg"
                >
                  {language === 'fr-FR' ? 'Parfait !' : 'Got it!'}
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Payment Modal */}
        <PaymentModal
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          onSuccess={() => {
            setShowPaymentModal(false);
            refreshUserData();
          }}
        />
      </div>
      </div>
    );
  }

  return (
    <>
      {/* Global Payment Status Messages */}
      {paymentStatus && paymentMessage && (
        <div className={`fixed top-4 left-1/2 transform -translate-x-1/2 z-50 p-4 rounded-lg shadow-lg ${
          paymentStatus === 'success' ? 'bg-green-500 text-white' :
          paymentStatus === 'error' ? 'bg-red-500 text-white' :
          'bg-blue-500 text-white'
        }`}>
          {paymentMessage}
        </div>
      )}
      
      {/* Payment Modal */}
      <PaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        onSuccess={() => {
          setShowPaymentModal(false);
          refreshUserData();
        }}
      />

      {/* Standard User Onboarding Modal */}
      <StandardOnboardingModal
        isOpen={showStandardOnboarding}
        onClose={() => {
          setShowStandardOnboarding(false);
          setHighlightField(null);
        }}
        onHighlightField={setHighlightField}
        onComplete={() => {
          setHighlightField(null);
        }}
      />

      {/* Standard User Reminder Modal */}
      <StandardReminderModal
        isOpen={showStandardReminder}
        onClose={() => setShowStandardReminder(false)}
      />
      
      {/* This should never render */}
      <div>Unknown step</div>
    </>
  );
};

export default BedtimeStoryGenerator;